import { Box, Flex } from "styled-system/jsx";
import { DoorstopCardProps } from "./DoorstopCard";
import { Button, Card, Text, useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { EnumUserAccessOperatorsStatus } from "gql/generated";
import { useMemo, useState } from "react";
import { useAppContext } from "AppContext";

import { useClaimAnOperator } from "shared/hooks";

type OperatorDoorstopCardProps = DoorstopCardProps;

export const OperatorDoorstopCard = ({
  doorstopItem,
  onItemClick,
}: OperatorDoorstopCardProps) => {
  const { name, address, claimed, _entityId } = doorstopItem;

  const [isClaimLoading, setIsClaimLoading] = useState(false);

  const { t } = useTranslation("core");
  const { dbUser } = useAppContext();
  const { toastFactory } = useToastContext();

  const isAccessRequested = useMemo(
    () =>
      dbUser?.access.operators.some(
        (operator) =>
          operator?.entityId === _entityId &&
          operator.status === EnumUserAccessOperatorsStatus.Requested,
      ),
    [dbUser, _entityId],
  );

  const { onClaimOperator } = useClaimAnOperator(_entityId);

  const handleItemClick = () => {
    if (isAccessRequested) {
      return;
    }
    onItemClick?.(doorstopItem);
  };

  const handleClaimClick = async () => {
    if (isAccessRequested) {
      return;
    }
    try {
      setIsClaimLoading(true);

      await onClaimOperator();

      toastFactory.create({
        title: t("SUCCESS"),
        description: t("DOORSTOP.CARD.CLAIMED", { businessName: name }),
      });

      handleItemClick();
    } catch {
      toastFactory.create({
        title: t("ERROR.TITLE"),
        description: t("DOORSTOP.CARD.ERROR_CLAIMED"),
      });
    } finally {
      setIsClaimLoading(false);
    }
  };
  return (
    <Card.Surface flex="1" minH="140px" minWidth="200px" bg={"white"}>
      <Card.Body
        display={"flex"}
        flexDir={{ base: "column", md: "row" }}
        gap={"100"}
      >
        <Flex flexDir={"column"} gap={"100"} w={"full"}>
          <Text visual={"bodySemiBold"} color={"text.dark"}>
            {name}
          </Text>
          {address && (
            <Text visual={"smallMedium"} color={"text.light"}>
              {address}
            </Text>
          )}
        </Flex>
        {/* Set minW to leave space for when button is loading */}
        <Box minW={{ base: "full", md: "130px" }} alignSelf={"flex-end"}>
          <Button
            size={"sm"}
            visual={claimed || isAccessRequested ? "primary" : "accent.blue"}
            mode={"light"}
            onClick={claimed ? handleItemClick : handleClaimClick}
            isLoading={isClaimLoading}
            disabled={isAccessRequested}
            justifySelf={"flex-end"}
          >
            {isAccessRequested
              ? t("DOORSTOP.CARD.ACCESS_PENDING")
              : claimed
                ? t("DOORSTOP.CARD.EDIT")
                : t("DOORSTOP.CARD.CLAIM")}
          </Button>
        </Box>
      </Card.Body>
    </Card.Surface>
  );
};
